import React, { useState, useEffect, useRef } from 'react';
import { Spin, Avatar } from 'antd';
import { User, Save, Upload as UploadIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import Inputs from '../../components/inputs';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { BUCKET_URL } from '../../config/config';
import { useFileUpload } from '../../hooks/useFileUpload';
import { getUserProfile, updateUserProfile, UserProfileData } from '../../services/userProfileService';
import '../../styles/designSystem.css';
import './settings.scss';
import logger from '../../utils/logger';

const UserProfileSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userData, setUserData] = useState<UserProfileData>({
    firstName: '',
    lastName: '',
    countryCode: '',
    phone: '',
    email: '',
    profilePicture: ''
  });

  // File upload hook
  const { uploadFile, uploading: uploadingPicture, progress } = useFileUpload({
    recordId: userData._id || 'temp',
    type: 'user',
    maxSize: 5,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
    onSuccess: (files) => {
      if (files && files.length > 0) {
        setUserData(prev => ({
          ...prev,
          profilePicture: files[0].filePath
        }));
        toast.success('Profile picture uploaded successfully');
      }
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error}`);
    }
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await getUserProfile();

      logger.log('[fetchUserProfile] Full API Response:', response);
      logger.log('[fetchUserProfile] Response.data keys:', Object.keys(response));
      logger.log('[fetchUserProfile] Response.data.data:', response.data);
      logger.log('[fetchUserProfile] Response.data.data keys:', Object.keys(response.data || {}));

      if (response?.data) {
        const userData = response.data;
        const profilePicturePath = userData.profile?.filePath || '';
        logger.log('[fetchUserProfile] Profile object:', userData.profile);
        logger.log('[fetchUserProfile] Profile picture path:', profilePicturePath);

        setUserData({
          _id: userData._id || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          countryCode: userData.countryCode || '',
          phone: userData.phone || '',
          email: userData.email || '',
          profilePicture: profilePicturePath
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!userData._id) {
      toast.error('User ID not found. Please refresh the page.');
      return;
    }

    await uploadFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Exclude profilePicture - it's already uploaded via useFileUpload hook
      const { profilePicture, ...dataToUpdate } = userData;

      logger.log('[handleSave] Sending update data:', dataToUpdate);

      await updateUserProfile(dataToUpdate);

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-content-wrapper">
        <div className="ds-flex ds-items-center ds-justify-center" style={{ minHeight: '20rem' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-content-wrapper">
      <div className="ds-card" style={{ maxWidth: '60rem', margin: '0 auto' }}>
        {/* Profile Picture */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Profile Picture</h3>
            <p className="helper-text">Upload a profile picture to personalize your account. This image will be displayed across the platform.</p>
          </div>
          <div className="ds-flex ds-flex-col ds-items-center ds-gap-4" style={{ padding: '1rem 0' }}>
            <Avatar
              size={80}
              src={userData.profilePicture ? `${BUCKET_URL}${userData.profilePicture}` : undefined}
              icon={<User />}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={handleProfilePictureUpload}
              style={{ display: 'none' }}
            />
            <button
              className="ds-btn ds-btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPicture}
            >
              <UploadIcon size={16} />
              {uploadingPicture ? `Uploading... ${progress}%` : 'Upload Picture'}
            </button>
            <p className="ds-body-sm ds-text-tertiary" style={{ textAlign: 'center', margin: 0 }}>
              Max size: 5MB. Supported: JPG, PNG, GIF
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Personal Information</h3>
            <p className="helper-text">Manage your personal details. Your name and contact information help team members identify and communicate with you.</p>
          </div>
          <div className="ds-form-grid">
            <Inputs
              label="First Name"
              name="firstName"
              type="name"
              value={userData.firstName}
              onChange={handleInputChange}
              placeholder="Enter your first name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-firstname"
            />
            <Inputs
              label="Last Name"
              name="lastName"
              type="name"
              value={userData.lastName}
              onChange={handleInputChange}
              placeholder="Enter your last name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-lastname"
            />
            <Inputs
              label="Email"
              name="email"
              type="email"
              value={userData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-email"
              disabled={true}
            />
            <div>
              <label className="ds-label">Phone</label>
              <PhoneInput
                country="in"
                value={`${userData.countryCode}${userData.phone}`}
                onChange={() => {}}
                containerClass="react-tel-input"
                disabled={true}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="ds-card-footer">
          <button
            className="ds-btn ds-btn-primary ds-btn-lg"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'Saving Changes...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;
